import React, { useState, useEffect } from 'react';

const Alert = ({ show = false, message, type = 'success', duration = 3000, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (show && message) {
      setVisible(true);
      setIsClosing(false);
      
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
      setIsClosing(false);
    }
  }, [show, message, duration]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setVisible(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!visible || !show || !message) return null;

  const getAlertStyles = () => {
    const baseStyles = {
      position: 'fixed',
      top: '20px',
      right: '20px',
      minWidth: '300px',
      maxWidth: '500px',
      padding: '16px 20px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      transition: 'all 0.3s ease',
      transform: isClosing ? 'translateX(120%)' : 'translateX(0)',
      opacity: isClosing ? 0 : 1
    };

    const typeStyles = {
      success: {
        backgroundColor: '#10b981',
        color: 'white'
      },
      error: {
        backgroundColor: '#ef4444',
        color: 'white'
      },
      warning: {
        backgroundColor: '#f59e0b',
        color: 'white'
      },
      info: {
        backgroundColor: '#3b82f6',
        color: 'white'
      }
    };

    return { ...baseStyles, ...typeStyles[type] };
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '✓';
    }
  };

  return (
    <div style={getAlertStyles()}>
      <div style={{
        fontSize: '20px',
        fontWeight: 'bold',
        lineHeight: '1',
        marginTop: '2px'
      }}>
        {getIcon()}
      </div>
      <div style={{ flex: 1, fontSize: '14px', lineHeight: '1.5' }}>
        {message}
      </div>
      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          fontSize: '18px',
          lineHeight: '1',
          padding: '0',
          opacity: 0.7,
          transition: 'opacity 0.2s'
        }}
        onMouseEnter={(e) => e.target.style.opacity = 1}
        onMouseLeave={(e) => e.target.style.opacity = 0.7}
      >
        ×
      </button>
    </div>
  );
};

let alertQueue = [];
let currentAlert = null;

export const showAlert = (message, type = 'success', duration = 3000) => {
  alertQueue.push({ message, type, duration });
  if (!currentAlert) {
    displayNextAlert();
  }
};

const displayNextAlert = () => {
  if (alertQueue.length === 0) {
    currentAlert = null;
    return;
  }

  const alertData = alertQueue.shift();
  currentAlert = alertData;

  const container = document.getElementById('alert-container');
  if (!container) {
    const newContainer = document.createElement('div');
    newContainer.id = 'alert-container';
    document.body.appendChild(newContainer);
  }

  const AlertComponent = () => (
    <Alert
      show={true}
      message={alertData.message}
      type={alertData.type}
      duration={alertData.duration}
      onClose={displayNextAlert}
    />
  );

  import('react-dom/client').then(({ createRoot }) => {
    const container = document.getElementById('alert-container');
    const root = createRoot(container);
    root.render(<AlertComponent />);
  });
};

export default Alert;
